import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from './types';

// =============================================================================
// App-wide state. Domain data (servers, channels, messages) lives in the
// route-level hooks since it changes constantly. This store is just the
// global session-y stuff.
// =============================================================================

// All available theme ids. Adding a new theme means: (1) define it in
// index.css under :root[data-theme="<id>"], (2) add an entry here.
export const THEMES = [
  { id: 'light', label: 'Classic light', desc: 'Warm parchment with AIM-blue panels', isDark: false },
  { id: 'dark', label: 'Warm dark', desc: 'Cozy charcoal with amber accents', isDark: true },
  { id: 'aim-blue', label: 'AIM classic blue', desc: '90s instant-messenger blue/cyan', isDark: false },
  { id: 'true-dark', label: 'True dark', desc: 'OLED-friendly near-black, high contrast', isDark: true },
  { id: 'midnight', label: 'Midnight', desc: 'Deep navy with cool cyan accents', isDark: true },
  { id: 'sepia', label: 'Sepia', desc: 'Warm parchment with amber tones', isDark: false },
] as const;

export type Theme = typeof THEMES[number]['id'];

export interface IncomingCall {
  dmChannelId: string;
  from: {
    id: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
}

export interface OutgoingCall {
  dmChannelId: string;
  toName: string;
}

interface AppState {
  user: User | null;
  setUser: (user: User | null) => void;

  theme: Theme;
  setTheme: (theme: Theme) => void;

  soundsEnabled: boolean;
  setSoundsEnabled: (v: boolean) => void;

  /** Toast "You're muted" when the mic hears speech while self-muted. */
  mutedSpeechHint: boolean;
  setMutedSpeechHint: (v: boolean) => void;

  // Audio capture preferences — applied when joining voice
  audioSettings: AudioSettings;
  setAudioSettings: (patch: Partial<AudioSettings>) => void;
  screenShareSettings: ScreenShareSettings;
  setScreenShareSettings: (patch: Partial<ScreenShareSettings>) => void;
  cameraSettings: CameraSettings;
  setCameraSettings: (patch: Partial<CameraSettings>) => void;
  // Selected mic + speaker device ids. null = system default.
  audioDevices: AudioDeviceSettings;
  setAudioDevices: (patch: Partial<AudioDeviceSettings>) => void;

  // Voice connection — lives at app level so navigating away from a voice
  // channel doesn't disconnect. Cleared explicitly on user "leave".
  activeVoice: {
    serverId: string;
    channelId: string;
    channelName: string;
    /** True when this is a 1:1 DM call (room is the DM channel's voice room).
     *  Drives DM-aware navigation (return-to-call goes to /dm/:id). */
    isDm?: boolean;
    /** Set for an instant meeting room. When present, VoiceConnection mints its
     *  token from the meeting endpoints instead of /api/voice/token — a meeting
     *  is not a channel, so `channelId` here holds the room CODE. */
    meetCode?: string;
  } | null;
  setActiveVoice: (v: AppState['activeVoice']) => void;

  // Instant meeting room session. Held separately from `user` because a guest
  // has no account: this is the entire identity a guest carries. Persisted so a
  // refresh mid-meeting rejoins rather than dumping them back to a name prompt.
  meetSession: {
    code: string;
    /** Room-scoped credential for the meeting chat endpoints (X-Meet-Token). */
    meetToken: string;
    /** LiveKit participant identity, so the panel can mark its own messages. */
    identity: string;
    /** The participant's own display name (what a guest typed at the door). */
    displayName: string;
    /** The meeting's name, for the header and the rejoin card. */
    roomName: string;
    /** LiveKit media credentials, held here so VoiceConnection can connect
     *  without a second round trip — and so a page refresh mid-meeting
     *  reconnects with the SAME participant identity. Re-joining instead would
     *  mint a fresh `g_…` identity, resetting everyone's per-participant volume
     *  overrides and making one person appear to leave and rejoin. */
    livekitUrl: string;
    livekitToken: string;
    expiresAt: number;
    isHost: boolean;
  } | null;
  setMeetSession: (v: AppState['meetSession']) => void;

  // DM call signaling (the in-app ring UX). Transient — not persisted.
  // `incomingCall` is set when someone rings us; `outgoingCall` while we're
  // ringing someone and waiting for them to answer.
  incomingCall: IncomingCall | null;
  setIncomingCall: (v: IncomingCall | null) => void;
  outgoingCall: OutgoingCall | null;
  setOutgoingCall: (v: OutgoingCall | null) => void;

  // True while the LiveKit (voice/video) connection is reconnecting after a
  // network blip. Separate from the WS gateway connection — LiveKit runs on a
  // different host and reconnects independently. Surfaced by ConnectionBanner.
  // Transient (not persisted); written by the VoiceConnectionWatcher inside
  // the LiveKitRoom tree.
  voiceReconnecting: boolean;
  setVoiceReconnecting: (v: boolean) => void;

  // Persisted hint that we WERE in voice — used after a page refresh to offer
  // a one-click rejoin. Cleared once we either rejoin or the user dismisses.
  lastVoiceChannelId: string | null;
  lastVoiceChannelAt: number | null;
  setLastVoice: (channelId: string | null) => void;

  // Per-participant volume overrides (local only — only affects what THIS
  // user hears). Keyed by user id. Value is a multiplier where 1.0 = default,
  // 0 = muted (for me), 2.0 = +6dB. Persists across sessions so re-joining
  // voice doesn't reset everyone's volume to default.
  participantVolumes: Record<string, number>;
  setParticipantVolume: (userId: string, volume: number) => void;
  resetParticipantVolume: (userId: string) => void;

  // Per-screen-share audio volume. Separate from participantVolumes because
  // screen-share audio is a distinct audio track (tab/system audio) that
  // listeners may want to balance differently from voice — e.g. the sharer's
  // voice is fine but the YouTube video they're sharing is too loud.
  // Keyed by sharer's user id; preference persists across sessions.
  screenShareVolumes: Record<string, number>;
  setScreenShareVolume: (userId: string, volume: number) => void;
  resetScreenShareVolume: (userId: string) => void;

  // Track who's online (userIds). Updated by WS presence events.
  onlineUserIds: Set<string>;
  setUserOnline: (userId: string, online: boolean) => void;
  setOnlineUserIds: (ids: string[]) => void;
  resetPresence: () => void;

  // Unread state — shared across hook instances. This must live in the store
  // (rather than per-hook useState) because multiple components subscribe to
  // unreads (AppShell, DmSidebar) and they need to see consistent state. With
  // separate per-hook state, marking a channel as read in one instance
  // wouldn't update the other.
  unreadByChannel: Record<string, { unreadCount: number; mentionCount: number }>;
  setUnreadByChannel: (next: Record<string, { unreadCount: number; mentionCount: number }>) => void;
  clearUnread: (channelId: string) => void;
  bumpUnread: (channelId: string, isMention: boolean) => void;
  // Tracks when a channel was last cleared client-side; used to filter stale
  // /unreads responses where last_read_at hasn't propagated yet.
  unreadClearedAt: Record<string, number>;
}

export interface AudioSettings {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  // Custom noise gate (in addition to browser noiseSuppression). Mutes the
  // mic when input level is below thresholdDb. Useful for keyboard clicks
  // and breathing during silences.
  noiseGateEnabled: boolean;
  noiseGateThresholdDb: number;  // typically -60 to -20 dB
}

// =============================================================================
// Screen share quality settings.
// =============================================================================
// Independent of audio because the tradeoffs are different — share quality
// is mostly a "how much upload bandwidth do I want to use" question, while
// mic processing is about voice cleanup. Bitrate is derived from the
// resolution+framerate combination rather than user-configurable, since
// nobody wants to think in Mbps. The numbers below are tuned for typical
// home internet (5-10 Mbps up) producing clearly-readable text and smooth
// motion video.
export type ScreenShareResolution = 720 | 1080;
export type ScreenShareFramerate = 30 | 60;
export interface ScreenShareSettings {
  resolution: ScreenShareResolution;
  framerate: ScreenShareFramerate;
  /** Whether to capture the computer's audio along with the screen. Off by
   *  default in the browser, because a whole-screen/window share loops the
   *  call's own audio back and everyone hears themselves echo. The desktop
   *  client's per-app capture avoids that, so it defaults on there. */
  shareAudio: boolean;
}

const DEFAULT_SCREEN_SHARE_SETTINGS: ScreenShareSettings = {
  resolution: 720,
  framerate: 30,
  // Default on only on the desktop client (per-app capture = no echo);
  // default off in browsers to prevent the mic-feedback echo.
  shareAudio: typeof window !== 'undefined' && !!window.electronAPI?.isDesktop,
};

/** Returns the recommended max bitrate (bits per second) for a given
 *  share quality combination. Tuned conservatively — these aren't streaming
 *  service numbers, just enough to look great for a friend group call. */
export function bitrateForShareSettings(s: ScreenShareSettings): number {
  // bps; multiplied by 1_000_000 for clarity at the call site
  if (s.resolution === 1080 && s.framerate === 60) return 6_000_000;
  if (s.resolution === 1080) return 4_000_000;        // 1080p30
  if (s.framerate === 60) return 4_000_000;            // 720p60
  return 2_500_000;                                    // 720p30 (default)
}

// Webcam quality preset — same philosophy as ScreenShareSettings (a couple
// of sane combinations, not user-facing Mbps). Cameras are auto-subscribed
// by everyone in the room, so the ceiling is deliberately lower than screen
// share: 1080p webcam feeds cost every viewer download bandwidth for little
// visible gain at tile sizes.
export type CameraResolution = 360 | 720 | 1080;
export type CameraFramerate = 15 | 30;
export interface CameraSettings {
  resolution: CameraResolution;
  framerate: CameraFramerate;
}

const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  resolution: 720,
  framerate: 30,
};

/** Capture dimensions for a camera resolution preset (16:9). */
export function dimensionsForCameraResolution(res: CameraResolution): {
  width: number;
  height: number;
} {
  if (res === 1080) return { width: 1920, height: 1080 };
  if (res === 360) return { width: 640, height: 360 };
  return { width: 1280, height: 720 };
}

const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  // Noise gate ON by default. Most environments have low-level background
  // noise (fans, keyboards, hum) that the browser's noiseSuppression alone
  // doesn't fully catch. The gate cuts the mic when input is below the
  // threshold so silence is actually silent.
  noiseGateEnabled: true,
  // -50 dB is a conservative floor — quiet enough to let normal speech
  // through (typical voice is -25 to -15 dBFS at sensible mic distance)
  // while cutting most ambient hiss. Users with louder backgrounds can
  // raise this in Settings to push out keyboard/keyboard-rattle noise too.
  noiseGateThresholdDb: -50,
};

// =============================================================================
// Audio device selection.
// =============================================================================
// Distinct from AudioSettings (which controls processing) because the choice
// of physical device is more like a system-config knob than a feature
// preference. Stored as device IDs (the opaque strings the browser hands
// out via enumerateDevices). null means "use system default" — useful when
// devices aren't yet enumerated at startup or the previously-selected
// device is gone (unplugged headset, etc).
//
// IMPORTANT: device IDs are tied to the browser+page+permission session.
// Re-granting mic permission, switching browser profiles, or some browser
// upgrades can change them. Code that consumes these IDs should fall back
// gracefully when the ID isn't present in the live device list.
export interface AudioDeviceSettings {
  micDeviceId: string | null;
  outputDeviceId: string | null;
  /** Selected webcam (videoinput) device. null = system default / first
   *  available. Same caveats as the audio IDs above re: stability across
   *  permission re-grants — consumers fall back gracefully when it's gone. */
  cameraDeviceId: string | null;
}

const DEFAULT_AUDIO_DEVICE_SETTINGS: AudioDeviceSettings = {
  micDeviceId: null,
  outputDeviceId: null,
  cameraDeviceId: null,
};

export const useApp = create<AppState>()(
  persist(
    (set) => ({
      user: null,
      setUser: (user) => set({ user }),

      theme: 'midnight',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      soundsEnabled: true,
      setSoundsEnabled: (v) => set({ soundsEnabled: v }),

      mutedSpeechHint: true,
      setMutedSpeechHint: (v) => set({ mutedSpeechHint: v }),

      audioSettings: DEFAULT_AUDIO_SETTINGS,
      setAudioSettings: (patch) =>
        set((s) => ({ audioSettings: { ...s.audioSettings, ...patch } })),
      screenShareSettings: DEFAULT_SCREEN_SHARE_SETTINGS,
      setScreenShareSettings: (patch) =>
        set((s) => ({ screenShareSettings: { ...s.screenShareSettings, ...patch } })),
      cameraSettings: DEFAULT_CAMERA_SETTINGS,
      setCameraSettings: (patch) =>
        set((s) => ({ cameraSettings: { ...s.cameraSettings, ...patch } })),
      audioDevices: DEFAULT_AUDIO_DEVICE_SETTINGS,
      setAudioDevices: (patch) =>
        set((s) => ({ audioDevices: { ...s.audioDevices, ...patch } })),

      activeVoice: null,
      meetSession: null,
      setMeetSession: (v) => set({ meetSession: v }),
      setActiveVoice: (v) => {
        set({ activeVoice: v });
        if (v) {
          set({ lastVoiceChannelId: v.channelId, lastVoiceChannelAt: Date.now() });
        } else {
          // Leaving voice clears any lingering reconnect indicator.
          set({ voiceReconnecting: false });
        }
      },

      incomingCall: null,
      setIncomingCall: (v) => set({ incomingCall: v }),
      outgoingCall: null,
      setOutgoingCall: (v) => set({ outgoingCall: v }),

      voiceReconnecting: false,
      setVoiceReconnecting: (v) => set({ voiceReconnecting: v }),

      lastVoiceChannelId: null,
      lastVoiceChannelAt: null,
      setLastVoice: (channelId) =>
        set({
          lastVoiceChannelId: channelId,
          lastVoiceChannelAt: channelId ? Date.now() : null,
        }),

      onlineUserIds: new Set(),
      setUserOnline: (userId, online) =>
        set((s) => {
          const next = new Set(s.onlineUserIds);
          if (online) next.add(userId);
          else next.delete(userId);
          return { onlineUserIds: next };
        }),
      setOnlineUserIds: (ids) => set({ onlineUserIds: new Set(ids) }),
      resetPresence: () => set({ onlineUserIds: new Set() }),

      participantVolumes: {},
      setParticipantVolume: (userId, volume) =>
        set((s) => ({
          participantVolumes: { ...s.participantVolumes, [userId]: volume },
        })),
      resetParticipantVolume: (userId) =>
        set((s) => {
          const next = { ...s.participantVolumes };
          delete next[userId];
          return { participantVolumes: next };
        }),

      screenShareVolumes: {},
      setScreenShareVolume: (userId, volume) =>
        set((s) => ({
          screenShareVolumes: { ...s.screenShareVolumes, [userId]: volume },
        })),
      resetScreenShareVolume: (userId) =>
        set((s) => {
          const next = { ...s.screenShareVolumes };
          delete next[userId];
          return { screenShareVolumes: next };
        }),

      unreadByChannel: {},
      unreadClearedAt: {},
      setUnreadByChannel: (next) => set({ unreadByChannel: next }),
      clearUnread: (channelId) =>
        set((s) => {
          const next = { ...s.unreadByChannel };
          delete next[channelId];
          return {
            unreadByChannel: next,
            unreadClearedAt: { ...s.unreadClearedAt, [channelId]: Date.now() },
          };
        }),
      bumpUnread: (channelId, isMention) =>
        set((s) => {
          const cur = s.unreadByChannel[channelId] ?? { unreadCount: 0, mentionCount: 0 };
          const nextCleared = { ...s.unreadClearedAt };
          // New activity — invalidate the prior clear so refresh()'s freshness
          // check doesn't drop genuine new unreads.
          delete nextCleared[channelId];
          return {
            unreadByChannel: {
              ...s.unreadByChannel,
              [channelId]: {
                unreadCount: cur.unreadCount + 1,
                mentionCount: cur.mentionCount + (isMention ? 1 : 0),
              },
            },
            unreadClearedAt: nextCleared,
          };
        }),
    }),
    {
      name: 'chatapp-app',
      partialize: (s) => ({
        theme: s.theme,
        soundsEnabled: s.soundsEnabled,
        mutedSpeechHint: s.mutedSpeechHint,
        audioSettings: s.audioSettings,
        screenShareSettings: s.screenShareSettings,
        cameraSettings: s.cameraSettings,
        audioDevices: s.audioDevices,
        lastVoiceChannelId: s.lastVoiceChannelId,
        lastVoiceChannelAt: s.lastVoiceChannelAt,
        participantVolumes: s.participantVolumes,
        screenShareVolumes: s.screenShareVolumes,
        meetSession: s.meetSession,
      }),
      // Defensive merge: explicitly fall back to current state defaults for
      // any persisted key. Without this, persisted snapshots from an older
      // build (before we added `participantVolumes`, `screenShareVolumes`,
      // etc.) load those fields as `undefined`. Code paths reading them
      // with `[id]` then throw "Cannot read properties of undefined" and
      // crash the whole app at render time.
      //
      // Zustand's default merge is a shallow Object.assign(current, persisted)
      // which does the right thing for fields that exist in both, but lets
      // missing fields stay missing. We explicitly fill in defaults from
      // the freshly-initialized state for any object-typed field so they're
      // always-real objects.
      merge: (persisted, current) => {
        const p = (persisted as Record<string, unknown>) ?? {};
        return {
          ...current,
          ...p,
          // Object-typed fields: always fall back to {} if missing/non-object,
          // so `state.x[id]` never throws.
          participantVolumes:
            isPlainObject(p.participantVolumes)
              ? (p.participantVolumes as Record<string, number>)
              : current.participantVolumes,
          screenShareVolumes:
            isPlainObject(p.screenShareVolumes)
              ? (p.screenShareVolumes as Record<string, number>)
              : current.screenShareVolumes,
          audioSettings: isPlainObject(p.audioSettings)
            ? { ...current.audioSettings, ...(p.audioSettings as object) }
            : current.audioSettings,
          screenShareSettings: isPlainObject(p.screenShareSettings)
            ? { ...current.screenShareSettings, ...(p.screenShareSettings as object) }
            : current.screenShareSettings,
          cameraSettings: isPlainObject(p.cameraSettings)
            ? { ...current.cameraSettings, ...(p.cameraSettings as object) }
            : current.cameraSettings,
          audioDevices: isPlainObject(p.audioDevices)
            ? { ...current.audioDevices, ...(p.audioDevices as object) }
            : current.audioDevices,
        };
      },
    }
  )
);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function applyTheme(theme: Theme): void {
  // Use a data-theme attribute (rather than a class) so multiple themes can
  // coexist in CSS as `:root[data-theme="..."]` blocks. We also still set a
  // class so any old/external code expecting `.dark` etc. doesn't break,
  // but new theme blocks key off the attribute.
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);

  // Pick a meta theme-color that matches the theme's bg. Browsers (especially
  // mobile Safari/Chrome) use this to color the address bar.
  const meta = THEMES.find((t) => t.id === theme);
  const isDark = meta?.isDark ?? false;
  // Quick lookup of the bg color via getComputedStyle on the root, so we
  // don't have to duplicate the hex values here.
  requestAnimationFrame(() => {
    const bg = getComputedStyle(root).getPropertyValue('--c-bg').trim();
    if (bg) {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
    } else {
      document.querySelector('meta[name="theme-color"]')?.setAttribute(
        'content',
        isDark ? '#1A1815' : '#F5EFE0'
      );
    }
  });
}

// Apply on initial load
const initial = useApp.getState().theme;
applyTheme(initial);
